export interface Form {
  [name: string]: {
    type: 'option' | 'text' | 'password' | 'date' | 'radio' | 'textArea';
    value?: string | { key: string; label: string }[];
    rules?: string | Record<string, unknown>;
    label: string;
    maxlength?: number;
    infoIcon?: string;
    disabled?: boolean;
    default?: string;
    onChange?: { clear: string[] } | 'trim' | 'number' | 'toUpperCase' | 'clear';
    featureFlag?: string;
  };
}

export const checkoutForm = (form?: Record<string, unknown>): Form => {
  const { $domain, $i18n, $config } = useNuxtApp();

  const currentForm = {
    ...(form ?? {}),
    ...(form?.country ? { country: form.country } : { country: $domain.countryCode }),
  } as { userType: 'company'; country?: string };

  const isPlB2b = currentForm?.userType === 'company' && $domain.countryCode === 'PL';

  return {
    salutation: {
      type: 'option',
      value: [
        { key: 'Mr.', label: 'Mr.' },
        { key: 'Mrs.', label: 'Mrs.' },
        { key: $domain.salutationOtherValue, label: $domain.salutationOtherValue },
      ],
      rules: {
        required: !isPlB2b,
      },
      label: 'Salutation',
    },
    title: {
      type: 'text',
      label: 'Title',
      rules: 'noEmojis',
    },
    firstName: {
      type: 'text',
      rules: {
        ...(isPlB2b ? { required: false } : { required: true, minmax: [2, 20] }),
        noEmojis: true,
      },
      label: 'First name',
    },
    lastName: {
      type: 'text',
      rules: {
        ...(isPlB2b ? { required: false } : { required: true, minmax: [2, 30] }),
        noEmojis: true,
      },
      label: 'Last name',
    },
    streetName: {
      type: 'text',
      rules: 'required|max:240|noEmojis',
      label: 'Street name',
    },
    streetNumber: {
      type: 'text',
      rules: 'required|max:16|noEmojis',
      label: 'House number',
    },
    additionalStreetInfo: {
      type: 'text',
      rules: 'max:50|noEmojis',
      label: 'Address addition',
    },
    packstation: {
      type: 'text',
      rules: 'required|noEmojis',
      label: 'Packstation',
      value: 'Packstation',
    },
    packstationNumber: {
      type: 'text',
      rules: 'required|minmax:1,5|numeric|noEmojis',
      label: 'Packstation number',
    },
    packstationPostNumber: {
      type: 'text',
      rules: 'required|minmax:6,10|numeric|noEmojis',
      label: 'Post number',
    },
    pickupLocationInformation: {
      type: 'text',
      rules: 'required',
      label: 'Desired location',
    },
    postalCode: {
      type: 'text',
      rules: `required|validZipCode:${currentForm?.country}|noEmojis`,
      label: 'Postal code',
      onChange: 'toUpperCase',
    },
    city: {
      type: 'text',
      rules: 'required|max:256|noEmojis',
      label: 'City',
    },
    country: {
      type: 'option',
      rules: 'required|noEmojis',
      label: 'Country',
      value: $config.public.i18n?.countries.map(({ name, label }) => ({ key: name, label })),
      default: currentForm?.country,
    },
    email: {
      type: 'text',
      label: 'E-mail address',
      rules: 'required|minmax:2,50|email|noEmojis',
      onChange: 'trim',
    },
    emailConfirmation: {
      type: 'text',
      label: 'Repeat e-mail address',
      rules: 'required|minmax:2,50|confirmed:@email|noEmojis',
      onChange: 'trim',
    },
    phone: {
      type: 'text',
      label: 'Phone number',
      rules: 'max:25|noEmojis',
    },
    mobile: {
      type: 'text',
      label: 'Mobile number',
      rules: 'max:25|noEmojis',
    },
    password: {
      type: 'password',
      label: 'Password',
      rules: 'required|password|noEmojis',
      infoIcon: $i18n.t('passwordNotValid'),
    },
    passwordConfirmation: {
      type: 'password',
      label: 'Repeat Password',
      rules: 'required|confirmed:@password|noEmojis',
    },
    dateOfBirth: {
      type: 'date',
      label: 'Date of birth',
      rules: {
        required: $domain.dateOfBirthRequired,
        dateFormat: true,
        dateUntilToday: true,
        noEmojis: true,
      },
    },
    companyName: {
      type: 'text',
      label: 'Company Name',
      rules: 'required|max:70|noEmojis',
    },
    vatId: {
      type: 'text',
      label: 'VAT',
      rules: `required|noEmojis|vatFormat:${currentForm?.country}`,
    },
    userType: {
      type: 'radio',
      label: '',
      value: [{ key: 'company', label: 'Company Type' }, { key: 'personal', label: 'Personal Type' }],
      default: 'personal',
      onChange: { clear: ['companyName', 'vatId'] },
      featureFlag: 'address.company-data',
    },
  };
};

export const checkoutKioskForm = () => ({
  firstName: {
    type: 'text',
    rules: 'required|minmax:2,20|noEmojis',
    label: 'First name',
  },
  lastName: {
    type: 'text',
    rules: 'required|minmax:2,30|noEmojis',
    label: 'Last name',
  },
  email: {
    type: 'text',
    label: 'E-mail address',
    rules: 'required|minmax:2,50|email|noEmojis',
    onChange: 'trim',
  },
  emailConfirmation: {
    type: 'text',
    label: 'Repeat e-mail address',
    rules: 'required|minmax:2,50|confirmed:@email|noEmojis',
    onChange: 'trim',
  },
});

export const shippingForm = (form?: Record<string, unknown>) => {
  const { $domain, $config } = useNuxtApp();

  return {
    ...checkoutForm(form),
    country: {
      type: 'option',
      rules: 'required',
      label: 'Country',
      disabled: true,
      value: $config.public.i18n?.countries.map(({ name, label }) => ({ key: name, label })),
      default: $domain.countryCode?.toUpperCase(),
    },
  };
};

export const invoiceForm = () => {
  const { $domain } = useNuxtApp();
  return {
    dateOfBirth: {
      type: 'date',
      label: 'Date of birth',
      rules: {
        required: $domain.dateOfBirthRequired,
        dateFormat: true,
        dateUntilToday: true,
        atAgeLimit: 18,
        validBirthDate: true,
      },
    },
  };
};

export const packStationForm = (form?: Record<string, unknown>) => {
  const { $flags } = useNuxtApp();
  return {
    ...shippingForm(form),
    userType: {
      type: 'radio',
      label: '',
      value: [
        { key: 'packstation', label: 'Packstation' },
        ...($flags.has('address.company-data') ? [{ key: 'company', label: 'Company Type', disabled: true }] : []),
        { key: 'personal', label: 'Personal Type', disabled: true },
      ],
      default: 'packstation',
      onChange: { clear: ['packstation', 'packstationNumber', 'packstationPostNumber', 'pickupLocationInformation'] },
    },
  };
};

export const newShippingForm = (form?: Record<string, unknown>) => {
  const { $flags } = useNuxtApp();
  return {
    ...shippingForm(form),
    userType: {
      type: 'radio',
      label: '',
      value: [
        { key: 'packstation', label: 'Packstation' },
        ...($flags.has('address.company-data') ? [{ key: 'company', label: 'Company Type' }] : []),
        { key: 'personal', label: 'Personal Type' },
      ],
      default: 'personal',
      onChange: { clear: ['packstation', 'packstationNumber', 'packstationPostNumber', 'pickupLocationInformation', 'companyName', 'vatId'] },
    },
  };
};
